import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Grid, Link, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import { useState, ReactNode, useCallback, FC, useEffect, ChangeEvent } from 'react';
import { WsColors, Label, Item } from 'wisestep-base';

import { getMonthList } from '../features/submissions/submissionListing/helper/menuList';

const LinkTag = styled(Link)(() => ({
  color: WsColors.TheBlues500,
  cursor: 'pointer',
}));

export interface IEducationType {
  org: string;
  degree: string;
  major: string;
  periodStart: {
    year: number | null;
    month: number | null;
    day: number | null;
  };
  periodEnd: {
    year: number | null;
    month: number | null;
    day: number | null;
  };
}

interface IEducationField {
  initialValue: IEducationType[] | null | [];
  onInputChange(args: IEducationType[]): void;
}

const EducationField: FC<IEducationField> = ({ initialValue, onInputChange }) => {
  const months = getMonthList;
  const [linksValue, setLinksValue] = useState<IEducationType[]>(
    !isEmpty(initialValue) && initialValue
      ? initialValue
      : [
          {
            org: '',
            degree: '',
            major: '',
            periodStart: {
              year: null,
              month: null,
              day: null,
            },
            periodEnd: {
              year: null,
              month: null,
              day: null,
            },
          },
        ]
  );
  const [totalLinks, setTotalLinks] = useState<ReactNode[]>([]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number, field: 'org' | 'degree' | 'major') => {
      setLinksValue((linksValue: IEducationType[]) => {
        linksValue[index][field] = event.target.value;
        const newValues = [...linksValue];
        onInputChange(linksValue);
        return newValues;
      });
    },
    [onInputChange]
  );

  const handleDateChange = useCallback(
    (
      event: SelectChangeEvent<unknown>,
      index: number,
      field: 'periodStart' | 'periodEnd',
      subField: 'month' | 'year'
    ) => {
      if (event) {
        setLinksValue((linksValue: IEducationType[]) => {
          linksValue[index][field][subField] = parseInt(event.target.value as string);
          linksValue[index][field]['day'] = 1;
          const newValues = [...linksValue];
          onInputChange(linksValue);
          return newValues;
        });
      }
    },
    [onInputChange]
  );

  const addEduInput = () => {
    if (isEmpty(totalLinks)) {
      setTotalLinks([eduInputWithRemoveIcon(0)]);
    } else {
      setTotalLinks([...totalLinks, eduInputWithRemoveIcon(totalLinks.length)]);
    }
    setLinksValue([
      ...linksValue,
      {
        org: '',
        degree: '',
        major: '',
        periodStart: {
          year: null,
          month: null,
          day: null,
        },
        periodEnd: {
          year: null,
          month: null,
          day: null,
        },
      },
    ]);
  };

  const removeEduLink = useCallback(
    (index: number) => {
      setTotalLinks((totalLinks) => {
        delete totalLinks[index];
        return [...totalLinks];
      });
      setLinksValue((linksValue) => {
        delete linksValue[index + 1];
        const newValues = [...linksValue];
        onInputChange(newValues);
        return newValues;
      });
    },
    [onInputChange]
  );

  const eduInputWithRemoveIcon = useCallback(
    (index: number) => (
      <Item key={index} sx={{ border: '1px solid ', mt: 2 }}>
        <RemoveCircleOutlineIcon
          onClick={() => removeEduLink(index)}
          sx={{
            color: WsColors.Red400,
            float: 'right',
            cursor: 'pointer',
            fontSize: '32px',
          }}
        />
        <Grid container>
          <Grid item xs={6}>
            <Label>University Name</Label>
            <TextField
              sx={{ width: '90%' }}
              value={linksValue[index + 1]?.org}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="Add university name"
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, index + 1, 'org')}
              variant="outlined"
              autoComplete="candidate_education_university"
            />
          </Grid>
          <Grid item xs={6}>
            <Label>Degree</Label>
            <TextField
              sx={{ width: '90%' }}
              value={linksValue[index + 1]?.degree}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="Add degree"
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, index + 1, 'degree')}
              variant="outlined"
              autoComplete="candidate_education_degree"
            />
          </Grid>
          <Grid item xs={6}>
            <Label>Major</Label>
            <TextField
              sx={{ width: '90%' }}
              value={linksValue[index + 1]?.major}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="Add major"
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, index + 1, 'major')}
              variant="outlined"
              autoComplete="candidate_education_major"
            />
          </Grid>
          <Grid item xs={6}></Grid>
          <Grid item xs={6} mt={2}>
            <Label>Started At</Label>
            <Select
              sx={{ height: '47px', minWidth: '126px' }}
              placeholder="month"
              onChange={(event: SelectChangeEvent<unknown>) =>
                handleDateChange(event, index + 1, 'periodStart', 'month')
              }
              value={linksValue[index + 1]?.periodStart.month}
            >
              {months.map((obj, index: number) => (
                <MenuItem key={index} value={obj.index}>
                  {obj.month}
                </MenuItem>
              ))}
            </Select>
            <TextField
              sx={{ ml: 2, width: '45%' }}
              value={linksValue[index + 1]?.periodStart.year}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="year"
              variant="outlined"
              type="number"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleDateChange(e, index + 1, 'periodStart', 'year')
              }
              autoComplete="candidate_messenger_id"
            />
          </Grid>
          <Grid item xs={6} mt={2}>
            <Label>Ended At</Label>
            <Select
              sx={{ height: '47px', minWidth: '126px' }}
              placeholder="month"
              value={linksValue[index + 1]?.periodEnd.month}
              onChange={(event: SelectChangeEvent<unknown>) =>
                handleDateChange(event, index + 1, 'periodEnd', 'month')
              }
            >
              {months.map((obj, index: number) => (
                <MenuItem key={index} value={obj.index}>
                  {obj.month}
                </MenuItem>
              ))}
            </Select>
            <TextField
              sx={{ ml: 2, width: '45%' }}
              value={linksValue[index + 1]?.periodEnd.year}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="year"
              variant="outlined"
              type="number"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleDateChange(e, index + 1, 'periodEnd', 'year')
              }
              autoComplete="candidate_messenger_id"
            />
          </Grid>
        </Grid>
      </Item>
    ),
    [handleChange, handleDateChange, linksValue, months, removeEduLink]
  );

  useEffect(() => {
    if (!isEmpty(linksValue) && isEmpty(totalLinks)) {
      const links: ReactNode[] = [];
      linksValue.forEach((link: IEducationType, index: number) => {
        index !== 0 && links.push(eduInputWithRemoveIcon(index - 1));
      });
      if (!isEmpty(links)) {
        setTotalLinks([...links]);
      }
    }
  }, [linksValue, eduInputWithRemoveIcon, totalLinks]);

  return (
    <Grid container direction="row">
      <Item sx={{ border: '1px solid ' }}>
        <Grid container>
          <Grid item xs={6}>
            <Label>University Name</Label>
            <TextField
              sx={{ width: '90%' }}
              value={linksValue[0]?.org}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="Add university name"
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, 0, 'org')}
              variant="outlined"
              autoComplete="candidate_education_university"
            />
          </Grid>
          <Grid item xs={6}>
            <Label>Degree</Label>
            <TextField
              sx={{ width: '90%' }}
              value={linksValue[0]?.degree}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="Add degree"
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, 0, 'degree')}
              variant="outlined"
              autoComplete="candidate_education_degree"
            />
          </Grid>
          <Grid item xs={6}>
            <Label>Major</Label>
            <TextField
              sx={{ width: '90%' }}
              value={linksValue[0]?.major}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="Add major"
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, 0, 'major')}
              variant="outlined"
              autoComplete="candidate_education_major"
            />
          </Grid>
          <Grid item xs={6}></Grid>
          <Grid item xs={6} mt={2}>
            <Label>Started At</Label>
            <Select
              sx={{ height: '47px', minWidth: '126px' }}
              placeholder="month"
              onChange={(event: SelectChangeEvent<unknown>) =>
                handleDateChange(event, 0, 'periodStart', 'month')
              }
              value={linksValue[0]?.periodStart.month}
            >
              {months.map((obj, index: number) => (
                <MenuItem key={index} value={obj.index}>
                  {obj.month}
                </MenuItem>
              ))}
            </Select>
            <TextField
              sx={{ ml: 2, width: '45%' }}
              value={linksValue[0]?.periodStart.year}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="year"
              variant="outlined"
              type="number"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleDateChange(e, 0, 'periodStart', 'year')
              }
              autoComplete="candidate_education_year"
            />
          </Grid>
          <Grid item xs={6} mt={2}>
            <Label>Ended At</Label>
            <Select
              sx={{ height: '47px', minWidth: '126px' }}
              placeholder="month"
              value={linksValue[0]?.periodEnd.month}
              onChange={(event: SelectChangeEvent<unknown>) =>
                handleDateChange(event, 0, 'periodEnd', 'month')
              }
            >
              {months.map((obj, index: number) => (
                <MenuItem key={index} value={obj.index}>
                  {obj.month}
                </MenuItem>
              ))}
            </Select>
            <TextField
              sx={{ ml: 2, width: '45%' }}
              value={linksValue[0]?.periodEnd.year}
              inputProps={{ style: { padding: '11.5px 14px' } }}
              placeholder="year"
              variant="outlined"
              type="number"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleDateChange(e, 0, 'periodEnd', 'year')
              }
              autoComplete="candidate_education_year"
            />
          </Grid>
        </Grid>
      </Item>
      {totalLinks.map((component: ReactNode) => component)}
      <Grid item xs={12}>
        <LinkTag underline="none" onClick={() => addEduInput()}>
          <Grid container direction="row" alignItems="center">
            <Grid item>
              <AddIcon sx={{ height: '20px', mt: '7px' }} />
            </Grid>
            <Grid item>Add More</Grid>
          </Grid>
        </LinkTag>
      </Grid>
    </Grid>
  );
};

export default EducationField;
